import ContractPricingProfilesCard from "@/assets/images/config/contract-pricing-profiles-card.svg";
import CustomRatesCard from "@/assets/images/config/custom-rates-card.svg";
import DefaultRatesCard from "@/assets/images/config/default-rates-card.svg";
import PricingDiagram from "@/assets/images/config/pricing-diagram.svg";
import { Button, Icon, Modal, Typography } from "@/components/atoms";

interface HowPricingWorksModalProps {
  open: boolean;
  onClose: () => void;
}

export const HowPricingWorksModal = ({ open, onClose }: HowPricingWorksModalProps) => {
  const featuredIcon = (
    <div className="flex">
      <div className="flex items-center justify-center rounded-full bg-warning-light p-2">
        <div className="flex items-center justify-center rounded-full bg-warning p-2">
          <Icon name="LampCharge" size="lg" className="flex-shrink-0" />
        </div>
      </div>
    </div>
  );

  const renderButtons = () => (
    <Button variant="secondary" size="lg" onClick={onClose} className="mt-8">
      Close
    </Button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-large"
      title="How Pricing Works + Examples"
      description="Simple Pricing Options for Your Business"
      featuredIcon={featuredIcon}
      renderButtons={renderButtons}
      className="!flex !max-h-[90vh] !flex-col !overflow-hidden"
      headerClassName="mb-5"
    >
      <div className="-mx-6 -mb-8 -mt-5 max-h-[60vh] overflow-y-auto px-6 pb-8 pt-5">
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          RideMinder helps you set fair prices for your customers with a smart system that gives you as much control as you need. You can
          choose how simple or detailed you want your pricing to be.
        </Typography>
        <div className="flex items-center justify-center">
          <img className="object-contain px-2 py-4 xl:px-6 xl:py-6" src={PricingDiagram} alt="pricing-diagram" />
        </div>
        <div className="flex flex-col gap-y-3 text-neutral-dark-gray">
          <div className="gapx-y-1 flex flex-col">
            <Typography variant="action">Pricing Hierarchy Explanation</Typography>
            <Typography variant="paragraph" className="mt-1">
              When calculating trip prices, the system follows this hierarchy:
            </Typography>
          </div>
          <Typography variant="paragraph">
            <ol className="ml-6 list-decimal space-y-2">
              <li>Contract Pricing Profile &ndash; If a company has a contract pricing profile attached, the system uses it first.</li>
              <li>
                Inside the Contract Pricing Profile:
                <ul className="ml-6 mt-1 list-disc space-y-1">
                  <li>It first checks if the trip falls under a Zone Rate (if set).</li>
                  <li>If no zone rate matches, it checks for a State Rate (if set).</li>
                </ul>
              </li>
              <li>
                Custom Rates &ndash; If no rates are found inside the contract pricing profile, the system checks if you&apos;ve set a
                custom rate based on state-level bookings.
              </li>
              <li>Default Rates &ndash; If no custom rates are found, the system falls back to the RideMinder system default rates.</li>
            </ol>
          </Typography>
        </div>
        <DefaultRatesDescription />
        <CustomRatesDescription />
        <ContractPricingProfilesDescription />
      </div>
    </Modal>
  );
};

const DefaultRatesDescription = () => (
  <>
    <div className="mt-12 flex flex-col overflow-hidden rounded-lg md:flex-row">
      <div className="flex-1 bg-neutral-surface-gray p-6">
        <div className="flex items-center gap-x-2 text-primary">
          <Typography variant="h3">Default Rates</Typography>
          <Icon isCustom name="speedtest" size="lg" className="flex-shrink-0" />
        </div>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          This is the fastest way to get started. RideMinder uses a sophisticated algorithm developed over decades to create fair market
          rates for your services.
        </Typography>
      </div>
      <div className="w-full md:w-[350px]">
        <img src={DefaultRatesCard} alt="default-rates-card" className="h-full w-full object-cover" />
      </div>
    </div>
    <div className="mt-2.5 flex flex-row gap-x-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>The system sets professionally-calibrated prices that are fair for both you and your customers</li>
            <li>You can adjust all prices up or down by a simple percentage (like 5%)</li>
            <li>Changes apply only to the default pricing, but won&apos;t affect any custom pricing you&apos;ve set up</li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>You&apos;re just getting started</li>
            <li>You want a simple pricing system based on industry standards</li>
            <li>You want pricing that&apos;s already optimized for the limousine market</li>
          </ol>
        </Typography>
      </div>
    </div>
  </>
);

const CustomRatesDescription = () => (
  <>
    <div className="mt-12 flex flex-col overflow-hidden rounded-lg md:flex-row">
      <div className="flex-1 bg-neutral-surface-gray p-6">
        <div className="flex items-center gap-x-2 text-primary">
          <Typography variant="h3">Custom Rates</Typography>
          <Icon name="Setting5" size="lg" className="flex-shrink-0" />
        </div>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          RideMinder follows a clear pricing priority per state. Use Custom Rates to add and configure your own pricing for each state.
        </Typography>
      </div>
      <div className="w-full md:w-[350px]">
        <img src={CustomRatesCard} alt="custom-rates-card" className="h-full w-full object-cover" />
      </div>
    </div>
    <div className="mt-2.5 flex flex-row gap-x-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>Select a state that you want to add custom rates to</li>
            <li>Setup different rates per kilometer for different distance ranges</li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>You operate in specific states with different pricing needs</li>
            <li>Your operating costs vary between states</li>
            <li>You have different competition or demand levels by state</li>
          </ol>
        </Typography>
      </div>
    </div>
  </>
);

const ContractPricingProfilesDescription = () => (
  <>
    <div className="mt-12 flex flex-col overflow-hidden rounded-lg md:flex-row">
      <div className="flex-1 bg-neutral-surface-gray p-6">
        <div className="flex items-center gap-x-2 text-primary">
          <Typography variant="h3">Contract Pricing Profiles</Typography>
          <Icon name="UserOctagon" size="lg" className="flex-shrink-0" />
        </div>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          RideMinder allows you to create contract pricing packages tailored to specific clients or companies. When calculating trip prices,
          the system will apply the <b>Zone Rate</b> first (if set), then the <b>State Rate</b>.
        </Typography>
      </div>
      <div className="w-full md:w-[350px]">
        <img src={ContractPricingProfilesCard} alt="contract-pricing-profiles-card" className="h-full w-full object-cover" />
      </div>
    </div>
    <div className="mt-2.5 flex flex-row gap-x-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="action" className="mt-2">
          Zone Rates
        </Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>Define specific geographic zones with a reference point and coverage radius</li>
            <li>Set special prices that apply only within these defined zones</li>
            <li>Perfect for high-demand areas like airports or business districts</li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="action" className="mt-2">
          Zone Rates
        </Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>A client/company have special areas where costs or demand are different</li>
            <li>A client/company serve specific venues or facilities regularly</li>
            <li>A client/company want premium pricing for high-traffic locations</li>
          </ol>
        </Typography>
      </div>
    </div>
    <div className="mt-2.5 flex flex-row gap-x-2.5 text-neutral-dark-gray">
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">How it works</Typography>
        <Typography variant="action" className="mt-2">
          State Rates
        </Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>Select a state that you want to add custom rates to</li>
            <li>Setup different fixed rate per range for different distance ranges</li>
          </ol>
        </Typography>
      </div>
      <div className="flex flex-1 flex-col rounded-lg bg-neutral-surface-gray p-4">
        <Typography variant="title">When to use it</Typography>
        <Typography variant="action" className="mt-2">
          State Rates
        </Typography>
        <Typography variant="paragraph" className="mt-2 list-inside list-decimal space-y-2">
          <ol className="list-outside list-decimal space-y-2 pl-5">
            <li>A client/company operate in specific states with different pricing needs</li>
            <li>A client/company operating costs very between states</li>
            <li>A client/company have different competition or demand levels by state</li>
          </ol>
        </Typography>
      </div>
    </div>
  </>
);
